import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './landing.css'; // This will contain your custom CSS animations

const LandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', body: '' });
  const [cookiesAccepted, setCookiesAccepted] = useState(false);

  useEffect(() => {
    // Check if cookies were already accepted on component mount
    if (localStorage.getItem('cookiesAccepted') === 'true') {
      setCookiesAccepted(true);
    }
  }, []);

  useEffect(() => {
    // --- Scroll Reveal Animation ---
    const revealElements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0.1 });

    revealElements.forEach(el => observer.observe(el));

    // --- Header Scroll Effect ---
    const header = document.getElementById('header');
    const handleScroll = () => {
      if (window.scrollY > 50) {
        header.classList.add('bg-[#111111]/80', 'backdrop-blur-lg', 'shadow-lg');
      } else {
        header.classList.remove('bg-[#111111]/80', 'backdrop-blur-lg', 'shadow-lg');
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup function for event listeners
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const openModal = (title, content) => {
    setModalContent({ title, body: content });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleAcceptCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setCookiesAccepted(true);
  };

  // Legal content
  const termsContent = `
    <h4 class="font-bold text-white text-lg">1. Acceptance of Terms</h4>
    <p>By accessing and using the Stackd application ("App"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
    <h4 class="font-bold text-white text-lg mt-6">2. Description of Service</h4>
    <p>Stackd provides users with a personal finance management tool designed for flexible budgeting and financial tracking. The service is provided "as-is" and we assume no responsibility for the timeliness, deletion, mis-delivery or failure to store any user communications or personalization settings.</p>
    <h4 class="font-bold text-white text-lg mt-6">3. User Conduct</h4>
    <p>You are responsible for all your activity in connection with the services and must comply with all applicable local, state, national, and international laws and regulations.</p>
    <p class="mt-8 text-sm text-gray-500">Last updated: August 18, 2025</p>
  `;

  const privacyContent = `
    <h4 class="font-bold text-white text-lg">1. Information We Collect</h4>
    <p>We collect information you provide directly to us, such as when you create an account, and information we get from your use of our services, like transaction data you input. We do not sell your personal data.</p>
    <h4 class="font-bold text-white text-lg mt-6">2. How We Use Information</h4>
    <p>We use the information we collect to provide, maintain, and improve our services, to develop new ones, and to protect Stackd and our users. We also use this information to offer you tailored content.</p>
    <h4 class="font-bold text-white text-lg mt-6">3. Data Security</h4>
    <p>We work hard to protect Stackd and our users from unauthorized access to or unauthorized alteration, disclosure or destruction of information we hold. We use encryption to keep your data private while in transit.</p>
    <p class="mt-8 text-sm text-gray-500">Last updated: August 18, 2025</p>
  `;

  const cookiePolicyContent = `
    <h4 class="font-bold text-white text-lg">What are cookies?</h4>
    <p>A cookie is a small text file that a website saves on your computer or mobile device when you visit the site. It enables the website to remember your actions and preferences over a period of time.</p>
    <h4 class="font-bold text-white text-lg mt-6">How do we use cookies?</h4>
    <p>We use cookies to understand and save your preferences for future visits and compile aggregate data about site traffic and site interaction so that we can offer better site experiences and tools in the future. We use essential cookies for site functionality and performance cookies to analyze site usage.</p>
    <p class="mt-8 text-sm text-gray-500">Last updated: August 18, 2025</p>
  `;

  return (
    <>
      <div className="antialiased">
        {/* Header */}
        <header id="header" className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <div className="text-2xl font-bold tracking-tighter text-gray-100 uppercase">STACKD</div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-400 hover:text-white transition-colors duration-300">Features</a>
              <a href="#testimonials" className="text-gray-400 hover:text-white transition-colors duration-300">Community</a>
            </nav>
            <Link to="/app" className="bg-[#05F56A] text-black font-bold py-2 px-5 rounded-lg hover:bg-white transition-all duration-300 transform hover:scale-105">Go To The App</Link>
          </div>
        </header>

        {/* Hero Section */}
        <main className="animated-gradient pt-32 md:pt-48 pb-24 md:pb-32">
          <div className="container mx-auto px-6 text-center">
            <div className="relative inline-block mb-8">
              <h1 className="glitch-text text-5xl md:text-7xl lg:text-8xl">STACKD</h1>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-100 mb-4 reveal">Your Money Isn't Monthly.</h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto reveal" style={{ transitionDelay: '150ms' }}>
              Forget the old rules. Stackd is the only app built for the hustle. Own your cash flow, day by day, stack by stack. This is your money. This is your move.
            </p>
            <div className="mt-10 flex justify-center space-x-4 reveal" style={{ transitionDelay: '300ms' }}>
              <Link to="/app" className="bg-[#05F56A] text-black font-bold py-3 px-8 rounded-lg text-lg hover:bg-white transition-all duration-300 transform hover:scale-105">Launch Now</Link>
            </div>
          </div>
        </main>

        {/* Features Section */}
        <section id="features" className="py-20 md:py-32 bg-[#1A1A1A]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold reveal">Build Your Stack. Control Your World.</h2>
              <p className="text-lg text-gray-400 mt-4 max-w-2xl mx-auto reveal" style={{ transitionDelay: '150ms' }}>No spreadsheets. No confusion. Just clarity and control.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-10">
              {/* Feature 1 */}
              <div className="bg-[#222222] p-8 rounded-2xl border border-gray-700/50 reveal">
                <div className="text-3xl font-bold text-[#05F56A] mb-4">01</div>
                <h3 className="text-2xl font-bold text-white mb-3">Cash Stacks</h3>
                <p className="text-gray-400">Your income and bills, finally making sense. Allocate your money the moment it hits your account. Daily, weekly, whatever. You run the clock.</p>
              </div>
              {/* Feature 2 */}
              <div className="bg-[#222222] p-8 rounded-2xl border border-gray-700/50 reveal" style={{ transitionDelay: '200ms' }}>
                <div className="text-3xl font-bold text-[#05F56A] mb-4">02</div>
                <h3 className="text-2xl font-bold text-white mb-3">Goal Stacks</h3>
                <p className="text-gray-400">That new gear, that trip, that next big thing. Build a Stack for it. Watch it grow. Make it happen. No more guessing, just progress.</p>
              </div>
              {/* Feature 3 */}
              <div className="bg-[#222222] p-8 rounded-2xl border border-gray-700/50 reveal" style={{ transitionDelay: '400ms' }}>
                <div className="text-3xl font-bold text-[#05F56A] mb-4">03</div>
                <h3 className="text-2xl font-bold text-white mb-3">Debt Crusher</h3>
                <p className="text-gray-400">Flip the script on debt. Our Debt Crusher organizes your payments into a clear-cut plan of attack. Watch your debt shrink and your freedom grow.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Parallax Image Section 1: Urban Environment */}
        <section className="parallax-section" style={{ backgroundImage: "url('https://iso.500px.com/wp-content/uploads/2015/09/salisbury_cover-1500x1000.jpeg')" }}>
          <div className="parallax-overlay">
            <div className="text-center text-white p-6">
              <h2 className="text-4xl md:text-5xl font-bold reveal">Designed for Your Grind.</h2>
              <p className="text-lg mt-4 max-w-2xl mx-auto reveal" style={{ transitionDelay: '150ms' }}>From the subway to the studio, Stackd moves with you.</p>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-20 md:py-32 bg-[#111111]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold reveal">The Word On The Street.</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="border border-gray-800 p-8 rounded-2xl reveal">
                <p className="text-gray-300 mb-6">"Finally, an app that gets that my income doesn't come on the 1st and 15th. Stackd just *flows*."</p>
                <div className="flex items-center">
                  <img src="https://placehold.co/40x40/222222/E5E7EB?text=JC" className="w-10 h-10 rounded-full" alt="User Avatar" />
                  <div className="ml-4">
                    <p className="font-bold text-white">@jess_creates</p>
                    <p className="text-sm text-gray-500">Content Creator</p>
                  </div>
                </div>
              </div>
              <div className="border border-gray-800 p-8 rounded-2xl reveal" style={{ transitionDelay: '200ms' }}>
                <p className="text-gray-300 mb-6">"Seeing my Goal Stacks build up is the only motivation I need. The app is clean AF."</p>
                <div className="flex items-center">
                  <img src="https://placehold.co/40x40/222222/E5E7EB?text=A" className="w-10 h-10 rounded-full" alt="User Avatar" />
                  <div className="ml-4">
                    <p className="font-bold text-white">Anton</p>
                    <p className="text-sm text-gray-500">Graphic Designer</p>
                  </div>
                </div>
              </div>
              <div className="border border-gray-800 p-8 rounded-2xl reveal" style={{ transitionDelay: '400ms' }}>
                <p className="text-gray-300 mb-6">"Used the Debt Crusher to map out my student loan payoff. For the first time, it actually feels possible. Game changer."</p>
                <div className="flex items-center">
                  <img src="https://placehold.co/40x40/222222/E5E7EB?text=GG" className="w-10 h-10 rounded-full" alt="User Avatar" />
                  <div className="ml-4">
                    <p className="font-bold text-white">@gigwork_grind</p>
                    <p className="text-sm text-gray-500">Freelancer</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Parallax Image Section 2: Money/Wealth */}
        <section className="parallax-section" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1639322537228-f710d846310a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1932&q=80')" }}>
          <div className="parallax-overlay">
            <div className="text-center text-white p-6">
              <h2 className="text-4xl md:text-5xl font-bold reveal">It's Not Just Money. It's Fuel.</h2>
              <p className="text-lg mt-4 max-w-2xl mx-auto reveal" style={{ transitionDelay: '150ms' }}>Power your goals. Fund your life. Build your empire.</p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 md:py-32 bg-[#1A1A1A]">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-6xl font-extrabold text-white reveal">Stop Budgeting. Start Building.</h2>
            <p className="text-lg text-gray-400 mt-4 max-w-xl mx-auto reveal" style={{ transitionDelay: '150ms' }}>Your financial future isn't a spreadsheet. It's a lifestyle.</p>
            <div className="mt-10 reveal" style={{ transitionDelay: '300ms' }}>
              <Link to="/app" className="bg-[#05F56A] text-black font-bold py-4 px-10 rounded-lg text-xl hover:bg-white transition-all duration-300 transform hover:scale-105 inline-block">Own Your Stack</Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#111111] py-12">
          <div className="container mx-auto px-6 text-center text-gray-500">
            <p className="text-2xl font-bold tracking-tighter text-gray-100 uppercase mb-4">STACKD</p>
            <div className="flex justify-center space-x-6 mb-6">
              <button onClick={() => openModal('Terms of Service', termsContent)} className="hover:text-white transition-colors">Terms of Service</button>
              <button onClick={() => openModal('Privacy Policy', privacyContent)} className="hover:text-white transition-colors">Privacy Policy</button>
            </div>
            <p>&copy; 2025 STACKD. All Rights Reserved.</p>
          </div>
        </footer>

        {/* Cookie Consent Banner */}
        {!cookiesAccepted && (
          <div id="cookie-banner" className="fixed bottom-0 left-0 right-0 bg-[#222222] border-t border-gray-700 p-4 z-50 transform translate-y-full transition-transform duration-500 ease-in-out">
            <div className="container mx-auto flex flex-col md:flex-row items-center justify-between px-6">
              <p className="text-gray-300 mb-4 md:mb-0">We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies. <button onClick={() => openModal('Cookie Policy', cookiePolicyContent)} className="text-[#05F56A] hover:underline">Learn more</button>.</p>
              <button onClick={handleAcceptCookies} className="bg-[#05F56A] text-black font-bold py-2 px-6 rounded-lg hover:bg-white transition-colors duration-300 flex-shrink-0">Accept</button>
            </div>
          </div>
        )}

        {/* Modal Structure */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-md"></div>
            <div className="bg-[#1A1A1A] border border-gray-700 rounded-2xl shadow-xl w-full max-w-3xl max-h-[80vh] z-10 transform scale-100 transition-transform duration-300 ease-in-out">
              <div className="flex justify-between items-center p-6 border-b border-gray-700">
                <h3 className="text-2xl font-bold text-white">{modalContent.title}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-white">&times;</button>
              </div>
              <div className="p-8 overflow-y-auto max-h-[calc(80vh-140px)] text-gray-300 space-y-4" dangerouslySetInnerHTML={{ __html: modalContent.body }}></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LandingPage;